/**
 * Vendors, and where their money goes.
 *
 * The bank-account panel is the point of this screen. Changing a payee's
 * account is the classic fraud against a business this size, so an account is
 * never edited — a change is proposed and somebody else approves it, and the
 * screen shows that chain rather than a single editable field.
 *
 * The list finds a vendor; the vendor opens beside it with their details,
 * papers, bank accounts and payments, and whatever their approval waits on.
 */

import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, formatInr } from '../api'
import { useAuth } from '../auth'
import { Empty, ErrorNote, Field, Loading, Note, Pill, Stat } from '../components/ui'
import Attachments from '../components/Attachments'
import { Pager, usePaged } from '../components/Pager'
import { SidePanel, Tabs, rowProps } from '../components/SidePanel'

interface Vendor {
  id: string
  code: string
  name: string
  legal_name: string | null
  kind: string
  status: string
  gstin: string | null
  pan: string | null
  state_code: string | null
  msme_class: string
  payment_terms_days: number
  tds_section: string | null
  tds_head: string | null
  pan_status: string
  constitution: string
  transporter_declaration: boolean
  version: number
  approval_state: string
  category: string | null
  service_id: string | null
  service_name: string | null
  contact_person: string | null
  suggested_by: string | null
  suggested_by_user_id: string | null
}

interface Service {
  id: string
  code: string
  name: string
  is_active: boolean
  vendors: number
}

interface Person {
  id: string
  display_name: string
  is_active: boolean
}

interface BankAccount {
  id: string
  party_id: string
  account_name: string
  account_number_masked: string
  ifsc: string
  bank_name: string | null
  status: string
  version: number
  approved_at: string | null
  supersedes_id: string | null
  approvals_outstanding: number
  //: Where a payment goes when nobody chose an account. One per vendor.
  is_primary: boolean
}

const MSME = ['not_registered', 'micro', 'small', 'medium']
//: The heads in the rulepack. A vendor with none has nothing withheld, which is
//: right for most suppliers and wrong for the few it is not.
const TDS_HEADS = [
  '',
  'contractor',
  'professional_fees',
  'rent',
  'rent_by_non_audit_individual',
  'purchase_of_goods',
]
const PAN_STATES = ['unknown', 'valid', 'valid_inoperative', 'invalid', 'not_furnished']
const CONSTITUTIONS = [
  'other',
  'individual',
  'huf',
  'proprietorship',
  'partnership',
  'llp',
  'company',
  'trust',
]

interface ContractRow {
  id: string
  reference: string
  title: string
  contract_reference: string | null
  status: string
  frequency: string
  starts_on: string
  ends_on: string | null
  contract_value_paise: number
  committed_paise: number
  paid_paise: number
  remaining_paise: number
  unallocated_paise: number
  overdue_paise: number
  expiring_soon: boolean
  expired: boolean
}

interface ContractPosition {
  contract_count: number
  contract_value_paise: number
  committed_paise: number
  paid_paise: number
  remaining_paise: number
  unallocated_paise: number
  overdue: { count: number; amount_paise: number }
  payment_counts: Record<string, number>
  next_payment: {
    commitment_reference: string
    title: string
    due_on: string
    amount_paise: number
    note: string | null
    days_away: number
  } | null
  bills: {
    posted: number
    pending: number
    paid: number
    pending_paise: number
    awaited: number
  }
  contracts: ContractRow[]
}

//: Where this vendor stands, in money rather than in agreements.
//:
//: `committed` is what the schedules add up to and `contract value` is what
//: the paper says. They are shown separately rather than reconciled into one
//: number, because the gap between them is real: on a milestone contract it is
//: the part of the agreement nobody has scheduled yet, and collapsing it would
//: hide the thing somebody has to go and do.
function PaymentPosition({ vendor }: { vendor: Vendor }) {
  const position = useQuery({
    queryKey: ['vendor-position', vendor.id],
    queryFn: () => api.get<{ contracts: ContractPosition }>(`/parties/${vendor.id}/overview`),
    select: (data) => data.contracts,
  })

  if (position.isLoading) return <Loading what="Payments" />
  if (position.error) return <ErrorNote error={position.error} />
  const data = position.data
  if (!data || data.contract_count === 0) {
    return <Empty>No contracts with {vendor.name} yet.</Empty>
  }

  return (
    <div>
      <dl className="appr-facts">
        <div>
          <dt>Contract value</dt>
          <dd>
            {formatInr(data.contract_value_paise)}
            <div className="faint">
              {data.contract_count} contract{data.contract_count === 1 ? '' : 's'}
            </div>
          </dd>
        </div>
        <div>
          <dt>Committed</dt>
          <dd>
            {formatInr(data.committed_paise)}
            <div className="faint">
              {data.unallocated_paise > 0
                ? `${formatInr(data.unallocated_paise)} not yet scheduled`
                : 'the whole of it is scheduled'}
            </div>
          </dd>
        </div>
        <div>
          <dt>Paid</dt>
          <dd>
            {formatInr(data.paid_paise)}
            <div className="faint">settled against the bank</div>
          </dd>
        </div>
        <div>
          <dt>Remaining</dt>
          <dd>
            {formatInr(data.remaining_paise)}
            <div className="faint">scheduled and not yet paid</div>
          </dd>
        </div>
        <div>
          <dt>Overdue</dt>
          <dd>
            {formatInr(data.overdue.amount_paise)}
            <div className="faint">
              {data.overdue.count} payment{data.overdue.count === 1 ? '' : 's'} past its date
            </div>
          </dd>
        </div>
        <div>
          <dt>Next payment</dt>
          <dd>
            {data.next_payment ? formatInr(data.next_payment.amount_paise) : '—'}
            <div className="faint">
              {data.next_payment
                ? `${data.next_payment.due_on} — ${data.next_payment.title}`
                : 'nothing scheduled ahead'}
            </div>
          </dd>
        </div>
      </dl>

      <p className="muted">
        {data.bills.pending} bill{data.bills.pending === 1 ? '' : 's'} still owing{' '}
        {formatInr(data.bills.pending_paise)}, {data.bills.paid} settled
        {data.bills.awaited > 0 && (
          <>
            {' '}
            — and {data.bills.awaited} raised payment
            {data.bills.awaited === 1 ? ' has' : 's have'} no bill against{' '}
            {data.bills.awaited === 1 ? 'it' : 'them'} yet
          </>
        )}
        .
      </p>

      <div className="scroll-x">
        <table className="appr-table">
          <thead>
            <tr>
              <th>Contract</th>
              <th>Runs</th>
              <th className="num">Value</th>
              <th className="num">Committed</th>
              <th className="num">Paid</th>
              <th className="num">Remaining</th>
              <th className="num">Overdue</th>
            </tr>
          </thead>
          <tbody>
            {data.contracts.map((row) => (
              <tr key={row.id}>
                <td>
                  <strong>{row.title}</strong>
                  <div className="faint mono">{row.contract_reference ?? row.reference}</div>
                  <Pill value={row.status} />
                </td>
                <td className="muted date">
                  {row.starts_on} → {row.ends_on ?? 'open'}
                  {row.expired && <div className="faint">ended</div>}
                  {!row.expired && row.expiring_soon && <div className="faint">ending soon</div>}
                </td>
                <td className="num">
                  {row.contract_value_paise ? formatInr(row.contract_value_paise) : '—'}
                </td>
                <td className="num">
                  {formatInr(row.committed_paise)}
                  {row.unallocated_paise > 0 && (
                    <div className="faint">{formatInr(row.unallocated_paise)} unscheduled</div>
                  )}
                </td>
                <td className="num">{formatInr(row.paid_paise)}</td>
                <td className="num">{formatInr(row.remaining_paise)}</td>
                <td className="num">{row.overdue_paise ? formatInr(row.overdue_paise) : '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

const BLANK_VENDOR = {
  code: '',
  name: '',
  category: 'recurring',
  service_id: '',
  contact_person: '',
  suggested_by_user_id: '',
  gstin: '',
  state_code: '',
  msme_class: 'not_registered',
  payment_terms_days: 30,
  tds_section: '',
  tds_head: '',
  pan_status: 'unknown',
  constitution: 'other',
}

type Tab = 'all' | 'approved' | 'waiting' | 'drafts'

export default function Vendors() {
  const { can } = useAuth()
  const queryClient = useQueryClient()
  const [selected, setSelected] = useState<string | null>(null)
  const [returning, setReturning] = useState(false)
  const [reason, setReason] = useState('')
  const [newService, setNewService] = useState('')
  //: The vendor being corrected, if any. One form serves both jobs: an edit
  //: screen that differed from the one the vendor was written on would be a
  //: second place for the two to drift apart.
  const [editingId, setEditingId] = useState<string | null>(null)
  const [editReason, setEditReason] = useState('')
  //: The form panel is open: adding a vendor, or correcting `editingId`.
  const [formOpen, setFormOpen] = useState(false)
  const [tab, setTab] = useState<Tab>('all')

  //: Filters live in the query, not in the browser over whatever happened to
  //: load: recording what a vendor does is only worth doing if "who cleans
  //: for us?" can be asked of the whole list afterwards.
  const [filter, setFilter] = useState({ q: '', service_id: '', category: '' })
  const query = new URLSearchParams(Object.entries(filter).filter(([, v]) => v !== '')).toString()

  const vendors = useQuery({
    queryKey: ['parties', query],
    queryFn: () => api.get<Vendor[]>(`/parties${query ? `?${query}` : ''}`),
  })
  const services = useQuery({
    queryKey: ['vendor-services'],
    queryFn: () => api.get<Service[]>('/parties/services'),
  })
  //: The directory, not the people list: names of everybody, whatever the
  //: reader's row visibility. A clinic-scoped Book Keeper reads only their
  //: own clinic's people on the People screen, but the person who put a
  //: vendor forward is as often a Finance Head or an Admin at head office.
  const people = useQuery({
    queryKey: ['directory'],
    queryFn: () => api.get<Person[]>('/org/directory'),
    enabled: can('org.user.view'),
  })
  const accounts = useQuery({
    queryKey: ['party-banks', selected],
    queryFn: () => api.get<BankAccount[]>(`/parties/${selected}/bank-accounts`),
    enabled: !!selected,
  })

  const [form, setForm] = useState(BLANK_VENDOR)
  const [bank, setBank] = useState({
    account_name: '',
    account_number: '',
    ifsc: '',
    bank_name: '',
    //: '' adds it beside the vendor's other accounts; an id replaces that one.
    replaces_id: '',
  })
  //: Making an account primary, or retiring it: both need a written reason.
  const [acting, setActing] = useState<{ id: string; what: 'primary' | 'retire' } | null>(null)
  const [actReason, setActReason] = useState('')

  const body = () => ({
    ...form,
    kind: 'vendor',
    gstin: form.gstin || null,
    category: form.category || null,
    service_id: form.service_id || null,
    contact_person: form.contact_person || null,
    suggested_by_user_id: form.suggested_by_user_id || null,
    state_code: form.state_code || null,
    tds_section: form.tds_section || null,
    tds_head: form.tds_head || null,
    pan_status: form.pan_status,
    constitution: form.constitution,
  })

  const closeForm = () => {
    setFormOpen(false)
    setEditingId(null)
    setEditReason('')
    setForm(BLANK_VENDOR)
  }

  const create = useMutation({
    mutationFn: () =>
      editingId
        ? api.put<Vendor>(`/parties/${editingId}`, {
            ...body(),
            reason: editReason || null,
          })
        : api.post<Vendor>('/parties', body()),
    onSuccess: (made) => {
      const corrected = editingId
      closeForm()
      void queryClient.invalidateQueries({ queryKey: ['parties'] })
      // Straight to the vendor, because the next thing anybody does is
      // attach the agreement and propose the bank account.
      setSelected(corrected ?? made.id)
    },
  })

  //: Load a vendor into the form. Everything, not just the boxes somebody is
  //: likely to change: the API takes the whole record, so sending back a
  //: half-filled one would blank the rest.
  const startEditing = (v: Vendor) => {
    setForm({
      code: v.code,
      name: v.name,
      category: v.category ?? '',
      service_id: v.service_id ?? '',
      contact_person: v.contact_person ?? '',
      suggested_by_user_id: v.suggested_by_user_id ?? '',
      gstin: v.gstin ?? '',
      state_code: v.state_code ?? '',
      msme_class: v.msme_class,
      payment_terms_days: v.payment_terms_days,
      tds_section: v.tds_section ?? '',
      tds_head: v.tds_head ?? '',
      pan_status: v.pan_status,
      constitution: v.constitution,
    })
    setEditReason('')
    setEditingId(v.id)
    setSelected(null)
    setFormOpen(true)
  }

  const sendBack = useMutation({
    mutationFn: (vars: { id: string; reason: string }) =>
      api.post<Vendor>(`/parties/${vars.id}/return`, { reason: vars.reason }),
    onSuccess: () => {
      setReturning(false)
      setReason('')
      void queryClient.invalidateQueries({ queryKey: ['parties'] })
    },
  })

  const addService = useMutation({
    mutationFn: (vars: { code: string; name: string }) =>
      api.post<Service>('/parties/services', vars),
    onSuccess: (made) => {
      setNewService('')
      setForm((f) => ({ ...f, service_id: made.id }))
      void queryClient.invalidateQueries({ queryKey: ['vendor-services'] })
    },
  })

  const propose = useMutation({
    mutationFn: () =>
      api.post<BankAccount>(`/parties/${selected}/bank-accounts`, {
        ...bank,
        bank_name: bank.bank_name || null,
        replaces_id: bank.replaces_id || null,
      }),
    onSuccess: () => {
      setBank({ account_name: '', account_number: '', ifsc: '', bank_name: '', replaces_id: '' })
      void queryClient.invalidateQueries({ queryKey: ['party-banks'] })
    },
  })

  const actOnAccount = useMutation({
    mutationFn: (vars: { id: string; what: 'primary' | 'retire'; reason: string }) =>
      api.post<BankAccount>(
        `/parties/bank-accounts/${vars.id}/${vars.what === 'primary' ? 'make-primary' : 'retire'}`,
        { reason: vars.reason },
      ),
    onSuccess: () => {
      setActing(null)
      setActReason('')
      void queryClient.invalidateQueries({ queryKey: ['party-banks'] })
    },
  })

  const approveBank = useMutation({
    mutationFn: (id: string) => api.post<BankAccount>(`/parties/bank-accounts/${id}/approve`),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['party-banks'] }),
  })

  const submitVendor = useMutation({
    mutationFn: (id: string) => api.post<Vendor>(`/parties/${id}/submit`),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['parties'] }),
  })
  const approveVendor = useMutation({
    mutationFn: (id: string) => api.post<Vendor>(`/parties/${id}/approve`),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['parties'] }),
  })

  const rows = vendors.data ?? []
  const inTab = (v: Vendor) =>
    tab === 'all' ||
    (tab === 'approved' && v.status === 'approved') ||
    (tab === 'waiting' && v.status === 'submitted') ||
    (tab === 'drafts' && (v.status === 'draft' || v.status === 'returned'))
  const shown = rows.filter(inTab)
  const paged = usePaged(shown, `${tab}|${query}`)

  if (vendors.isLoading) return <Loading what="Loading vendors" />
  const current = rows.find((v) => v.id === selected)
  const editingVendor = rows.find((v) => v.id === editingId)
  const unapproved = rows.filter((v) => v.status === 'draft' || v.status === 'submitted').length
  const waiting = rows.filter((v) => v.status === 'submitted')
  const drafts = rows.filter((v) => v.status === 'draft' || v.status === 'returned')
  const approved = rows.filter((v) => v.status === 'approved')

  const step = (v: Vendor) => {
    if (v.status === 'submitted')
      return can('party.vendor.approve')
        ? { label: 'Waiting for you', tone: 'turn', detail: v.approval_state }
        : { label: 'With a checker', tone: 'submitted', detail: v.approval_state }
    if (v.status === 'draft' || v.status === 'returned')
      return can('party.vendor.submit')
        ? {
            label: v.status === 'returned' ? 'Correct and resubmit' : 'Ready to submit',
            tone: 'turn',
          }
        : { label: v.status === 'returned' ? 'Sent back' : 'Draft', tone: v.status }
    if (v.status === 'approved') return { label: 'Can be paid', tone: 'approved' }
    return { label: v.status.replace(/_/g, ' '), tone: v.status }
  }
  const stepCell = (v: Vendor) => {
    const s = step(v)
    return (
      <div className="next-step">
        <span className={`pill ${s.tone}`}>{s.label}</span>
        {'detail' in s && s.detail && <span className="faint">{s.detail}</span>}
      </div>
    )
  }

  return (
    <>
      <ErrorNote error={vendors.error} />

      <div className="bank-toolbar">
        {can('party.vendor.create') && (
          <button
            className="primary"
            onClick={() => {
              closeForm()
              setFormOpen(true)
            }}
          >
            Add a vendor
          </button>
        )}
      </div>

      <div className="grid">
        <Stat
          label="Approved vendors"
          value={String(approved.length)}
          note="a bill can be posted against these"
        />
        <Stat
          label="Waiting for approval"
          value={String(waiting.length)}
          note={can('party.vendor.approve') ? 'yours to approve or send back' : 'with a checker'}
        />
        <Stat
          label="Drafts and sent back"
          value={String(drafts.length)}
          note="to finish and submit"
        />
        <Stat
          label="MSME registered"
          value={String(rows.filter((v) => v.msme_class !== 'not_registered').length)}
          note="45-day payment clock under s.15"
        />
      </div>

      {unapproved > 0 && (
        <Note kind="warn">
          {unapproved === 1 ? 'One vendor is' : `${unapproved} vendors are`} not approved yet. A
          bill cannot be posted against an unapproved vendor — the refusal is in the posting code,
          not in this screen, so it holds however the bill is created.
        </Note>
      )}

      <section className="card wide">
        <header className="appr-toolbar">
          <Tabs
            tabs={[
              { key: 'all', label: 'All', count: rows.length },
              { key: 'approved', label: 'Approved', count: approved.length },
              {
                key: 'waiting',
                label: 'Waiting for approval',
                count: waiting.length,
                quietWhenZero: true,
              },
              {
                key: 'drafts',
                label: 'Drafts & sent back',
                count: drafts.length,
                quietWhenZero: true,
              },
            ]}
            value={tab}
            onChange={setTab}
          />
          <div className="appr-filters">
            <input
              type="search"
              placeholder="Name, code, contact, GSTIN"
              value={filter.q}
              onChange={(e) => setFilter({ ...filter, q: e.target.value })}
              aria-label="Search vendors"
            />
            <select
              value={filter.service_id}
              onChange={(e) => setFilter({ ...filter, service_id: e.target.value })}
              aria-label="Service"
            >
              <option value="">Any service</option>
              {(services.data ?? []).map((sv) => (
                <option key={sv.id} value={sv.id}>
                  {sv.name} ({sv.vendors})
                </option>
              ))}
            </select>
            <select
              value={filter.category}
              onChange={(e) => setFilter({ ...filter, category: e.target.value })}
              aria-label="How they are paid"
            >
              <option value="">Recurring and fixed</option>
              <option value="recurring">Recurring</option>
              <option value="fixed">Fixed</option>
            </select>
          </div>
        </header>
        <div className="body tight">
          {shown.length === 0 ? (
            <div className="empty">
              {rows.length === 0 && !query ? 'No vendors yet.' : 'No vendor matches that.'}
            </div>
          ) : (
            <table className="appr-table">
              <thead>
                <tr>
                  <th>Vendor</th>
                  <th>Service</th>
                  <th>GSTIN</th>
                  <th>Terms</th>
                  <th>TDS</th>
                  <th>Status</th>
                  <th>Next step</th>
                </tr>
              </thead>
              <tbody>
                {paged.rows.map((v) => (
                  <tr key={v.id} {...rowProps(() => setSelected(v.id), selected === v.id)}>
                    <td>
                      <div className="strong">{v.name}</div>
                      <div className="cell-sub">
                        <span className="ref">{v.code}</span>
                        {v.category && ` · ${v.category}`}
                        {v.msme_class !== 'not_registered' && ` · MSME ${v.msme_class}`}
                      </div>
                    </td>
                    <td>
                      {v.service_name ?? <span className="faint">—</span>}
                      {v.contact_person && <div className="cell-sub">{v.contact_person}</div>}
                    </td>
                    <td className="mono">{v.gstin ?? <span className="faint">—</span>}</td>
                    <td className="muted">{v.payment_terms_days} days</td>
                    <td className="muted">
                      {v.tds_section ?? <span className="faint">—</span>}
                      <div className="cell-sub">
                        {v.tds_head ? v.tds_head.replace(/_/g, ' ') : 'nothing withheld'}
                        {v.pan_status !== 'valid' &&
                          v.tds_head &&
                          ` · PAN ${v.pan_status.replace(/_/g, ' ')}`}
                      </div>
                    </td>
                    <td>
                      <Pill value={v.status} />
                    </td>
                    <td>{stepCell(v)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        <Pager page={paged.page} pages={paged.pages} onPage={paged.setPage} />
      </section>

      {current && (
        <SidePanel
          kicker="Vendor"
          title={current.name}
          sub={
            <>
              <span className="ref">{current.code}</span>
              {current.service_name && ` · ${current.service_name}`}
              {current.category && ` · ${current.category}`}
              {current.suggested_by && ` · put forward by ${current.suggested_by}`}
            </>
          }
          badge={stepCell(current)}
          wide
          onClose={() => {
            setSelected(null)
            setReturning(false)
            setReason('')
          }}
          foot={
            <>
              <ErrorNote error={submitVendor.error ?? approveVendor.error ?? sendBack.error} />
              {/* Returned as well as draft: sending a vendor back is only worth
                  doing if it can come forward again, and a send-back with no
                  way to resubmit is a dead end that looks like a rejection. */}
              {(current.status === 'draft' || current.status === 'returned') &&
                can('party.vendor.submit') && (
                  <button
                    className="primary"
                    disabled={submitVendor.isPending}
                    onClick={() => submitVendor.mutate(current.id)}
                  >
                    {current.status === 'returned' ? 'Submit again' : 'Submit for approval'}
                  </button>
                )}
              {current.status === 'submitted' && can('party.vendor.approve') && !returning && (
                <>
                  <button
                    className="primary"
                    disabled={approveVendor.isPending}
                    onClick={() => approveVendor.mutate(current.id)}
                  >
                    Approve
                  </button>
                  <button onClick={() => setReturning(true)}>Send back</button>
                </>
              )}
              {current.status === 'submitted' && can('party.vendor.approve') && returning && (
                // A returned vendor is not a rejected one: this is "fix the
                // PAN", not "we will not deal with them", and the reason is
                // what makes the two readable apart afterwards.
                <>
                  <input
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    placeholder="What needs correcting — required, and kept"
                    aria-label="What needs correcting"
                    style={{ flex: '1 1 240px' }}
                    autoFocus
                  />
                  <button
                    className="primary"
                    disabled={sendBack.isPending || !reason.trim()}
                    onClick={() => sendBack.mutate({ id: current.id, reason })}
                  >
                    Send back
                  </button>
                  <button
                    className="quiet"
                    onClick={() => {
                      setReturning(false)
                      setReason('')
                    }}
                  >
                    Cancel
                  </button>
                </>
              )}
              {current.status === 'submitted' && !can('party.vendor.approve') && (
                <span className="faint">With a checker — nothing for you to do.</span>
              )}
              {current.status !== 'submitted' && can('party.vendor.edit') && (
                <button onClick={() => startEditing(current)}>Edit details</button>
              )}
              {can('party.ledger.view') && (
                <>
                  <span className="spacer" />
                  <Link to={`/buying/vendors/${current.id}`}>Everything about this vendor →</Link>
                </>
              )}
            </>
          }
        >
          <section>
            <dl className="appr-facts">
              <div>
                <dt>Status</dt>
                <dd>
                  <Pill value={current.status} />
                  <div className="faint">{current.approval_state}</div>
                </dd>
              </div>
              <div>
                <dt>Contact</dt>
                <dd>{current.contact_person ?? '—'}</dd>
              </div>
              <div>
                <dt>GSTIN</dt>
                <dd className="mono">{current.gstin ?? '—'}</dd>
              </div>
              <div>
                <dt>Payment terms</dt>
                <dd>{current.payment_terms_days} days</dd>
              </div>
              <div>
                <dt>TDS</dt>
                <dd>
                  {current.tds_section ?? '—'}
                  <div className="faint">
                    {current.tds_head ? current.tds_head.replace(/_/g, ' ') : 'nothing withheld'}
                  </div>
                </dd>
              </div>
              <div>
                <dt>PAN · constitution</dt>
                <dd>
                  {current.pan_status.replace(/_/g, ' ')} ·{' '}
                  {current.constitution.replace(/_/g, ' ')}
                </dd>
              </div>
              <div>
                <dt>MSME</dt>
                <dd>
                  {current.msme_class === 'not_registered' ? 'not registered' : current.msme_class}
                </dd>
              </div>
            </dl>
            {current.msme_class !== 'not_registered' && (
              <Note kind="info">
                Registered {current.msme_class} enterprise: goods must be paid within 45 days under
                MSMED s.15, after which interest accrues at three times the RBI bank rate — and that
                interest is not deductible.
              </Note>
            )}
          </section>

          <section>
            <h3>
              Bank accounts{' '}
              <span className="hint">
                · never edited; each one is proposed and approved by someone else. Payments go to
                the primary unless a request chooses another.
              </span>
            </h3>
            <ErrorNote
              error={accounts.error ?? approveBank.error ?? propose.error ?? actOnAccount.error}
            />
            {(accounts.data ?? []).length === 0 ? (
              <Note kind="warn">
                No bank account yet. This vendor cannot be paid until one is proposed and approved.
              </Note>
            ) : (
              <div className="scroll-x">
                <table className="appr-table">
                  <thead>
                    <tr>
                      <th>Account</th>
                      <th>Number</th>
                      <th>IFSC</th>
                      <th>Status</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {(accounts.data ?? []).map((a) => (
                      <tr key={a.id}>
                        <td>
                          {a.account_name}
                          {a.bank_name && <div className="faint">{a.bank_name}</div>}
                        </td>
                        <td className="mono">{a.account_number_masked}</td>
                        <td className="mono">{a.ifsc}</td>
                        <td>
                          <Pill value={a.status} />{' '}
                          {a.is_primary && a.status === 'active' && (
                            <span className="pill turn">primary</span>
                          )}
                          {a.supersedes_id && a.status === 'proposed' && (
                            <div className="faint">
                              replaces{' '}
                              {(accounts.data ?? []).find((x) => x.id === a.supersedes_id)
                                ?.account_number_masked ?? 'an earlier one'}{' '}
                              once approved
                            </div>
                          )}
                          {a.status === 'proposed' &&
                            !a.supersedes_id &&
                            (accounts.data ?? []).some((x) => x.status === 'active') && (
                              <div className="faint">added beside the live account(s)</div>
                            )}
                        </td>
                        <td className="num">
                          {a.status === 'proposed' && can('party.vendor_bank.approve') && (
                            <button
                              className="small primary"
                              disabled={approveBank.isPending}
                              onClick={() => approveBank.mutate(a.id)}
                            >
                              Approve
                            </button>
                          )}
                          {a.status === 'proposed' && !can('party.vendor_bank.approve') && (
                            <span className="faint">awaiting a checker</span>
                          )}
                          {a.status === 'active' && can('party.vendor_bank.approve') && (
                            <span className="row-actions">
                              {!a.is_primary && (
                                <button
                                  className="small"
                                  onClick={() => {
                                    setActing({ id: a.id, what: 'primary' })
                                    setActReason('')
                                  }}
                                >
                                  Make primary
                                </button>
                              )}
                              <button
                                className="small quiet"
                                onClick={() => {
                                  setActing({ id: a.id, what: 'retire' })
                                  setActReason('')
                                }}
                              >
                                Retire
                              </button>
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {acting && (
              <div className="row" style={{ marginTop: 10 }}>
                <Field
                  label={
                    acting.what === 'primary'
                      ? `Make ${(accounts.data ?? []).find((x) => x.id === acting.id)?.account_number_masked ?? 'this'} the primary — why`
                      : `Retire ${(accounts.data ?? []).find((x) => x.id === acting.id)?.account_number_masked ?? 'this'} — why`
                  }
                  caption="kept on the audit trail"
                >
                  <input
                    value={actReason}
                    onChange={(e) => setActReason(e.target.value)}
                    autoFocus
                    placeholder={
                      acting.what === 'primary'
                        ? 'the vendor asked to be paid here by default'
                        : 'account closed by the vendor'
                    }
                  />
                </Field>
                <div className="shrink">
                  <button
                    className={acting.what === 'retire' ? 'danger' : 'primary'}
                    disabled={actOnAccount.isPending || actReason.trim().length < 3}
                    onClick={() => actOnAccount.mutate({ ...acting, reason: actReason.trim() })}
                  >
                    {acting.what === 'primary' ? 'Make primary' : 'Retire account'}
                  </button>
                </div>
                <div className="shrink">
                  <button
                    className="quiet"
                    onClick={() => {
                      setActing(null)
                      setActReason('')
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
            {acting?.what === 'retire' && (
              <Note kind="info">
                A payment already approved for this account stops at release and has to be raised
                again for another account.
              </Note>
            )}

            {can('party.vendor_bank.edit') && (
              <form
                onSubmit={(e) => {
                  e.preventDefault()
                  propose.mutate()
                }}
                style={{ marginTop: 12 }}
              >
                <div className="row">
                  <Field label="Account name">
                    <input
                      value={bank.account_name}
                      onChange={(e) => setBank({ ...bank, account_name: e.target.value })}
                      required
                    />
                  </Field>
                  <Field label="Account number">
                    <input
                      value={bank.account_number}
                      onChange={(e) => setBank({ ...bank, account_number: e.target.value })}
                      required
                    />
                  </Field>
                  <Field label="IFSC" caption="11 characters">
                    <input
                      value={bank.ifsc}
                      onChange={(e) => setBank({ ...bank, ifsc: e.target.value.toUpperCase() })}
                      maxLength={11}
                      required
                    />
                  </Field>
                  {(accounts.data ?? []).some((x) => x.status === 'active') && (
                    <Field label="This account" caption="once approved">
                      <select
                        value={bank.replaces_id}
                        onChange={(e) => setBank({ ...bank, replaces_id: e.target.value })}
                      >
                        <option value="">is added beside the others</option>
                        {(accounts.data ?? [])
                          .filter((x) => x.status === 'active')
                          .map((x) => (
                            <option key={x.id} value={x.id}>
                              replaces {x.account_number_masked}
                              {x.is_primary ? ' (primary)' : ''}
                            </option>
                          ))}
                      </select>
                    </Field>
                  )}
                  <div className="shrink">
                    <button className="primary" type="submit" disabled={propose.isPending}>
                      Propose account
                    </button>
                  </div>
                </div>
                <Note kind="warn">
                  This does not take effect until someone else approves it. Any payment to an
                  account changed in the last seven days is flagged for review.
                </Note>
              </form>
            )}
          </section>

          <section>
            <h3>Papers</h3>
            {/* `other`, because this one slot takes an agreement, a registration
                certificate and a GST certificate alike, and a narrower label
                would be wrong for two of the three. It used to send `supporting`,
                which is not a kind the server knows: the file was stored and the
                step that attaches it was refused, leaving an upload linked to
                nothing and an error that named neither. */}
            <Attachments
              subjectType="party"
              subjectId={current.id}
              kind="other"
              canAttach={can('doc.attachment.create')}
              label="Attach the agreement, registration certificate, GST certificate…"
            />
          </section>

          {can('party.ledger.view') && (
            <section>
              <h3>
                Payments{' '}
                <span className="hint">
                  · what the contracts with this vendor are worth, and where they stand
                </span>
              </h3>
              <PaymentPosition vendor={current} />
            </section>
          )}
        </SidePanel>
      )}

      {formOpen && can('party.vendor.create') && (
        <SidePanel
          kicker="Vendor"
          title={editingId ? `Correcting ${form.name}` : 'Add a vendor'}
          sub={
            editingId
              ? editingVendor?.status === 'approved'
                ? 'this vendor is approved: the tax and identity fields are the Finance Head’s'
                : 'sent back for correction — fix it and submit it again'
              : 'starts as a draft; submit it, and someone else approves it'
          }
          wide
          onClose={closeForm}
        >
          <ErrorNote error={create.error ?? addService.error} />
          {editingId && editingVendor?.status === 'approved' && (
            <Field label="Why it is changing" caption="required, and kept with the change">
              <input
                value={editReason}
                onChange={(e) => setEditReason(e.target.value)}
                placeholder="the GST registration was cancelled and re-issued"
              />
            </Field>
          )}
          <form
            onSubmit={(e) => {
              e.preventDefault()
              create.mutate()
            }}
          >
            <section>
              <h3>Who they are</h3>
              <div className="row">
                <Field label="Code">
                  <input
                    value={form.code}
                    onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
                    required
                  />
                </Field>
                <Field label="Name">
                  <input
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    required
                  />
                </Field>
                <Field
                  label="How they are paid"
                  caption="recurring bills again and again; fixed has an agreed scope"
                >
                  <select
                    value={form.category}
                    onChange={(e) => setForm({ ...form, category: e.target.value })}
                  >
                    <option value="recurring">Recurring</option>
                    <option value="fixed">Fixed</option>
                  </select>
                </Field>
              </div>
              <div className="row">
                <Field label="What they provide" caption="so they can be found later">
                  <select
                    value={form.service_id}
                    onChange={(e) => setForm({ ...form, service_id: e.target.value })}
                  >
                    <option value="">Choose…</option>
                    {(services.data ?? []).map((sv) => (
                      <option key={sv.id} value={sv.id}>
                        {sv.name}
                      </option>
                    ))}
                  </select>
                </Field>
                <Field
                  label="Not on the list?"
                  caption="added to the list for everybody, so spell it as you would look for it"
                >
                  <input
                    value={newService}
                    onChange={(e) => setNewService(e.target.value)}
                    placeholder="Biomedical waste"
                  />
                </Field>
                <div className="shrink">
                  <button
                    type="button"
                    className="small"
                    disabled={addService.isPending || !newService.trim()}
                    onClick={() =>
                      addService.mutate({
                        code: newService.trim().slice(0, 12).replace(/\s+/g, '_'),
                        name: newService.trim(),
                      })
                    }
                  >
                    Add service
                  </button>
                </div>
              </div>
              <div className="row">
                <Field label="Contact person" caption="who to ring about a bill">
                  <input
                    value={form.contact_person}
                    onChange={(e) => setForm({ ...form, contact_person: e.target.value })}
                  />
                </Field>
                <Field label="Suggested by" caption="who in the business put them forward">
                  <select
                    value={form.suggested_by_user_id}
                    onChange={(e) => setForm({ ...form, suggested_by_user_id: e.target.value })}
                  >
                    <option value="">Nobody in particular</option>
                    {(people.data ?? [])
                      // Active accounts only: somebody who has left cannot be
                      // the one putting a vendor forward today. The one named
                      // on the record being edited stays, deactivated or not,
                      // so opening the form does not quietly change it.
                      .filter(
                        (person) => person.is_active || person.id === form.suggested_by_user_id,
                      )
                      .map((person) => (
                        <option key={person.id} value={person.id}>
                          {person.display_name}
                          {person.is_active ? '' : ' (deactivated)'}
                        </option>
                      ))}
                  </select>
                </Field>
              </div>
            </section>
            <section>
              <h3>Tax and terms</h3>
              <div className="row">
                <Field label="GSTIN">
                  <input
                    value={form.gstin}
                    onChange={(e) => setForm({ ...form, gstin: e.target.value.toUpperCase() })}
                    maxLength={15}
                  />
                </Field>
                <Field label="MSME class" caption="sets the statutory payment clock">
                  <select
                    value={form.msme_class}
                    onChange={(e) => setForm({ ...form, msme_class: e.target.value })}
                  >
                    {MSME.map((m) => (
                      <option key={m} value={m}>
                        {m.replace('_', ' ')}
                      </option>
                    ))}
                  </select>
                </Field>
                <Field label="Payment terms (days)">
                  <input
                    type="number"
                    value={form.payment_terms_days}
                    onChange={(e) =>
                      setForm({ ...form, payment_terms_days: Number(e.target.value) })
                    }
                  />
                </Field>
              </div>
              <div className="row">
                <Field label="TDS section" caption="what people say — 194C">
                  <input
                    value={form.tds_section}
                    onChange={(e) => setForm({ ...form, tds_section: e.target.value })}
                    maxLength={10}
                  />
                </Field>
                <Field label="TDS head" caption="what decides the rate">
                  <select
                    value={form.tds_head}
                    onChange={(e) => setForm({ ...form, tds_head: e.target.value })}
                  >
                    {TDS_HEADS.map((h) => (
                      <option key={h} value={h}>
                        {h ? h.replace(/_/g, ' ') : 'none — nothing withheld'}
                      </option>
                    ))}
                  </select>
                </Field>
                <Field label="PAN status" caption="unknown means the 20% penal rate">
                  <select
                    value={form.pan_status}
                    onChange={(e) => setForm({ ...form, pan_status: e.target.value })}
                  >
                    {PAN_STATES.map((s) => (
                      <option key={s} value={s}>
                        {s.replace(/_/g, ' ')}
                      </option>
                    ))}
                  </select>
                </Field>
                <Field label="Constitution" caption="1% not 2% for an individual">
                  <select
                    value={form.constitution}
                    onChange={(e) => setForm({ ...form, constitution: e.target.value })}
                  >
                    {CONSTITUTIONS.map((c) => (
                      <option key={c} value={c}>
                        {c.replace(/_/g, ' ')}
                      </option>
                    ))}
                  </select>
                </Field>
              </div>
            </section>
            <div className="row">
              <div className="shrink">
                <button className="primary" type="submit" disabled={create.isPending}>
                  {editingId ? 'Save the correction' : 'Add vendor'}
                </button>
              </div>
              <div className="shrink">
                <button type="button" className="quiet" onClick={closeForm}>
                  Cancel
                </button>
              </div>
            </div>
          </form>
        </SidePanel>
      )}
    </>
  )
}
